"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowRightOutline18, IconCircleInfoOutline18, IconCodeOutline18 } from "nucleo-ui-outline-18";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CommentsSection } from "@/components/ui/comments-section";
import { PageLayout } from "@/components/ui/page-layout";
import { Skeleton } from "@/components/ui/skeleton";
import { slugifyName } from "@/lib/api/server-fetchers";
import { V2_ENDPOINT, X402_ENDPOINT_PREFIX } from "@/consts";
import { useActions, useTriggers, type ActionTriggerDefinition } from "@/hooks/useActionTriggerDefinitions";
import { useNodeStatistics, useTimeSeries } from "@/hooks/useWorkflowStatistics";
import { getSyntaxHighlightingStyle } from "@/lib/syntax-highlighting-style";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { formatConnectorName } from "@/utils/string";
import { useQuery } from "@tanstack/react-query";
import { Background, ReactFlow, type Edge, type Node } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useColorMode } from "@chakra-ui/react";
import Link from "next/link";
import { notFound } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { ConnectorLogo } from "@/app/(v2)/connectors/_components/ConnectorLogo";
import { JsonSchemaProperty } from "./types";
import { X402InputSection } from "./X402InputSection";
import { X402OutputSection } from "./X402OutputSection";
import { CopyJsonButton, generateDefaultInputs, type X402ActionResponse } from "./IntegrationHelpers";
import { DynamicNodePositioning, ResponsiveFitView, edgeTypes, nodeTypes } from "./IntegrationPreviewCanvas";
import { IntegrationSidebar } from "./IntegrationSidebar";
import { IntegrationHeader } from "./IntegrationHeader";
import { IntegrationInputFields } from "./IntegrationInputFields";
import { IntegrationOutputFields } from "./IntegrationOutputFields";
import type { ServerIntegrationDef } from "@/lib/api/server-fetchers";
interface IntegrationDetailPageProps {
  integrationType: string;
  type: string;
  serverIntegration?: (ServerIntegrationDef & {
    integrationType: string;
  }) | null;
}
export default function IntegrationDetailClient({
  integrationType,
  type,
  serverIntegration
}: IntegrationDetailPageProps) {
  const {
    colorMode
  } = useColorMode();
  const [searchQuery, setSearchQuery] = useState("");
  const [previewValues, setPreviewValues] = useState<Record<string, unknown>>({});
  const [formKey, setFormKey] = useState(0);
  // Get syntax highlighting style based on theme
  const isDark = colorMode === "dark";
  const syntaxStyle = getSyntaxHighlightingStyle(isDark);

  // Fetch actions and triggers
  const {
    triggers,
    isLoading: triggersLoading
  } = useTriggers();
  const {
    actions,
    isLoading: actionsLoading
  } = useActions();

  // Fetch action details from API (for all actions, cached with TanStack Query)
  const {
    data: actionApiResponse
  } = useQuery({
    queryKey: ["action-details", type],
    queryFn: async () => {
      const url = `${V2_ENDPOINT}/v1/actions/${type}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch action details: ${res.status} ${res.statusText}`);
      return res.json() as Promise<X402ActionResponse>;
    },
    enabled: integrationType === "action",
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  });

  // Derive example output from the API response
  const exampleOutput = actionApiResponse?.code === 0 ? actionApiResponse?.data?.exampleOutput : undefined;

  // Fetch connector types for logo display
  const connectorTypes = useConnectorsStore(state => state.connectorTypes);
  const loadConnectorTypes = useConnectorsStore(state_0 => state_0.loadConnectorTypes);
  useEffect(() => {
    if (connectorTypes.length === 0) {
      loadConnectorTypes();
    }
  }, [connectorTypes.length, loadConnectorTypes]);

  // Fetch statistics for this integration
  const nodeStats = useNodeStatistics(integrationType as "trigger" | "action", type);

  // Time range for charts (last 7 days) - memoized to prevent infinite loops
  const timeRange = useMemo(() => {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return {
      from: sevenDaysAgo.toISOString(),
      to: now.toISOString(),
      granularity: "hourly" as const
    };
  }, []); // Only calculate once on mount

  // Fetch usage time series
  const usageTimeSeries = useTimeSeries({
    nodeType: integrationType as "trigger" | "action",
    nodeId: type,
    metricType: "usage",
    ...timeRange,
    enabled: !!(integrationType && type)
  });

  // Fetch latency time series (only for actions)
  const latencyTimeSeries = useTimeSeries({
    nodeType: "action",
    nodeId: type,
    metricType: "latency",
    ...timeRange,
    enabled: integrationType === "action" && !!type
  });
  const isLoading = triggersLoading || actionsLoading;

  // Find the specific integration — use server data as fallback while client fetches
  const integration = useMemo(() => {
    if (!isLoading) {
      const allIntegrations = [...triggers.map((t: ActionTriggerDefinition) => ({
        ...t,
        integrationType: "trigger" as const
      })), ...actions.map((a: ActionTriggerDefinition) => ({
        ...a,
        integrationType: "action" as const
      }))];
      // Try exact type match first
      const found = allIntegrations.find(i => i.integrationType === integrationType && i.type === type);
      if (found) return found;
      // Fall back to slug match (e.g. human-readable x402 slugs like "get-weather-data")
      const slugMatch = allIntegrations.find(i_0 => i_0.integrationType === integrationType && i_0.name && slugifyName(i_0.name) === type);
      if (slugMatch) return slugMatch;
    }
    // Use server-provided definition while client data loads
    if (serverIntegration) return serverIntegration;
    return undefined;
  }, [triggers, actions, integrationType, type, isLoading, serverIntegration]);

  // Look up connector type definition for logo URL
  const connectorTypeDefinition = useMemo(() => {
    if (!integration?.connector?.type) return null;
    return connectorTypes.find(ct => ct.type === integration.connector.type);
  }, [integration?.connector?.type, connectorTypes]);

  // Filter fields based on search query and hidden flag (must be before early returns to maintain hook order)
  const filteredFields = useMemo<[string, JsonSchemaProperty][]>(() => {
    if (!integration) return [];
    const properties = integration.payloadSchema?.properties || {};

    // Filter out hidden fields first (e.g., endpointId for x402 endpoints)
    const visibleFields = (Object.entries(properties) as [string, JsonSchemaProperty][]).filter(([, fieldSchema]) => !fieldSchema.hidden);
    if (!searchQuery.trim()) {
      return visibleFields;
    }
    const query = searchQuery.toLowerCase();
    return visibleFields.filter(([fieldName, fieldSchema_0]) => {
      return fieldName.toLowerCase().includes(query) || fieldSchema_0.description?.toLowerCase().includes(query) || fieldSchema_0.type?.toLowerCase().includes(query);
    });
  }, [integration, searchQuery]);

  // Create nodes and edges for the visual canvas (must be before early returns to maintain hook order)
  const {
    visualNodes,
    visualEdges
  } = useMemo<{
    visualNodes: Node[];
    visualEdges: Edge[];
  }>(() => {
    if (!integration) return {
      visualNodes: [],
      visualEdges: []
    };
    const isTrigger = integration && integration.integrationType === "trigger";

    // Estimated node heights - adjust based on content
    const PLACEHOLDER_HEIGHT = 55;
    const VERTICAL_GAP = 35;

    // Estimate main node height based on whether there's payload content
    const hasPayloadContent = previewValues && Object.keys(previewValues).length > 0;
    const isScheduleTrigger = integration.type === "schedule";
    const hasCountdown = isScheduleTrigger && previewValues.nextRunAt;

    // Base height + additional height for payload items
    let estimatedMainHeight = 90; // Base height
    if (hasPayloadContent) {
      const payloadItemCount = Object.keys(previewValues).length;
      estimatedMainHeight += payloadItemCount * 25; // ~25px per payload field
    }
    if (hasCountdown) {
      estimatedMainHeight += 70; // Additional height for countdown section
    }
    const mainNode: Node = {
      id: "integration-preview",
      type: isTrigger ? "trigger" : "action",
      position: {
        x: 0,
        y: isTrigger ? 0 : PLACEHOLDER_HEIGHT + VERTICAL_GAP
      },
      data: {
        name: integration.name,
        type: integration.type,
        description: integration.description,
        platform: integration.platform,
        payload: previewValues,
        // Use preview values to update node display in real-time
        connector: {
          id: "preview"
        } // Provide connector with id to suppress "Connector required" warning on read-only page
      },
      draggable: false,
      selectable: false,
      focusable: false
    };
    if (isTrigger) {
      // For triggers, add an action placeholder below
      const actionPlaceholderY = estimatedMainHeight + VERTICAL_GAP;
      return {
        visualNodes: [mainNode, {
          id: "placeholder-action",
          type: "placeholder",
          position: {
            x: 0,
            y: actionPlaceholderY
          },
          data: {
            text: "Then execute actions (100+ available)",
            showTop: true,
            showBottom: false
          },
          draggable: false,
          selectable: false,
          focusable: false
        }],
        visualEdges: [{
          id: "e-trigger-action",
          source: "integration-preview",
          target: "placeholder-action",
          type: "workflow",
          style: {
            stroke: "#d4d4d8",
            strokeWidth: 2,
            strokeDasharray: "5, 5"
          },
          animated: true,
          data: {
            hideAddButton: true
          }
        }]
      };
    } else {
      // For actions, add trigger before and action after
      const mainNodeY = PLACEHOLDER_HEIGHT + VERTICAL_GAP;
      const bottomPlaceholderY = mainNodeY + estimatedMainHeight + VERTICAL_GAP;
      return {
        visualNodes: [{
          id: "placeholder-trigger-top",
          type: "placeholder",
          position: {
            x: 0,
            y: 0
          },
          data: {
            text: "Trigger with some event",
            showTop: false,
            showBottom: true
          },
          draggable: false,
          selectable: false,
          focusable: false
        }, mainNode, {
          id: "placeholder-action-bottom",
          type: "placeholder",
          position: {
            x: 0,
            y: bottomPlaceholderY
          },
          data: {
            text: "Then chain actions (100+ available)",
            showTop: true,
            showBottom: false
          },
          draggable: false,
          selectable: false,
          focusable: false
        }],
        visualEdges: [{
          id: "e-trigger-action",
          source: "placeholder-trigger-top",
          target: "integration-preview",
          type: "workflow",
          style: {
            stroke: "#d4d4d8",
            strokeWidth: 2,
            strokeDasharray: "5, 5"
          },
          animated: true,
          data: {
            hideAddButton: true
          }
        }, {
          id: "e-action-action",
          source: "integration-preview",
          target: "placeholder-action-bottom",
          type: "workflow",
          style: {
            stroke: "#d4d4d8",
            strokeWidth: 2,
            strokeDasharray: "5, 5"
          },
          animated: true,
          data: {
            hideAddButton: true
          }
        }]
      };
    }
  }, [integration, previewValues]);
  const isTrigger_0 = integration && integration.integrationType === "trigger";
  const isX402Endpoint = type.startsWith(X402_ENDPOINT_PREFIX);

  // Memoize to avoid creating a new object on every render, which would reset the JSON editor
  const quickTestDefaults = useMemo(() => generateDefaultInputs(integration?.payloadSchema), [integration?.payloadSchema]);

  // Some connectors are optional (e.g. optional: true in store) — match TestTab logic
  const isConnectorOptional = useMemo(() => {
    const ct_0 = integration?.connector?.type;
    if (!ct_0 || !integration) return false;
    return connectorTypes.find(c => c.type === ct_0)?.optional === true;
  }, [integration?.connector?.type, connectorTypes]);
  const requiresConnector = !!integration?.connector && !isConnectorOptional;

  // For x402 endpoints, parse cost from description and create clean description
  let cleanDescription = integration?.description;
  let costDisplay: string | null = null;
  if (isX402Endpoint && integration?.description) {
    // Parse "| Cost: X USDC on base" from end of description
    const costMatch = integration?.description.match(/\s*\|\s*Cost:\s*(.+)$/);
    if (costMatch) {
      cleanDescription = integration?.description.replace(/\s*\|\s*Cost:\s*.+$/, "").trim();
      costDisplay = costMatch[1].trim();
    }
  }

  // Filter out hidden fields from counts
  const properties_0 = integration?.payloadSchema?.properties || {};
  const visibleFieldNames = Object.entries(properties_0).filter(([, schema]) => !(schema as JsonSchemaProperty).hidden).map(([name]) => name);
  const requiredFields = (integration?.payloadSchema?.required || []).filter((name_0: string) => visibleFieldNames.includes(name_0));
  const resultFields = Object.keys(integration?.resultSchema?.properties || {});
  const resultRequiredFields = integration?.resultSchema?.required || [];

  // Show loading only if no server data is available either
  if (!integration) {
    if (isLoading) {
      return <PageLayout>
          <IntegrationDetailSkeleton />
        </PageLayout>;
    }
    notFound();
  }
  return <PageLayout variant="sidebar" header={{
    searchPlaceholder: "Search integrations...",
    onSearch: setSearchQuery
  }} sidebar={<IntegrationSidebar integrationType={integrationType} integration={integration} allFields={visibleFieldNames} requiredFields={requiredFields} resultFields={resultFields} resultRequiredFields={resultRequiredFields} nodeStats={nodeStats} usageTimeSeries={usageTimeSeries} latencyTimeSeries={latencyTimeSeries} quickTestDefaults={quickTestDefaults} requiresConnector={requiresConnector} />}>
      <IntegrationHeader integration={integration} isTrigger={!!isTrigger_0} connectorLogoUrl={connectorTypeDefinition?.logoUrl} cleanDescription={cleanDescription} costDisplay={costDisplay} />
      {/* Schema Documentation */}
      <div className="space-y-12">
        {/* Visual Preview */}
        {visualNodes.length > 0 && <div className="integration-preview-container border-ds-border-default dark:bg-ds-neutral-900 h-[40dvh] min-h-[300px] overflow-hidden rounded-lg border bg-zinc-50">
            <style>{`
                      .integration-preview-container .react-flow__handle {
                        width: 10px !important;
                        height: 10px !important;
                        background: white !important;
                        border-color: #d4d4d8 !important;
                        border-width: 1.5px !important;
                      }
                      .dark .integration-preview-container .react-flow__handle {
                        background: #27272a !important;
                        border-color: #52525b !important;
                        border-width: 1.5px !important;
                      }
                    `}</style>
            <ReactFlow nodes={visualNodes} edges={visualEdges} nodeTypes={nodeTypes} edgeTypes={edgeTypes} nodesDraggable={false} nodesConnectable={false} nodesFocusable={false} edgesFocusable={false} elementsSelectable={false} panOnDrag={false} zoomOnScroll={false} zoomOnPinch={false} zoomOnDoubleClick={false} preventScrolling={false} className="dark:bg-ds-neutral-900 bg-zinc-50" proOptions={{
          hideAttribution: true
        }}>
              <DynamicNodePositioning isTrigger={isTrigger_0} />
              <ResponsiveFitView nodes={visualNodes} />
              <Background className="bg-ds-neutral-100 dark:bg-ds-neutral-800" gap={20} />
            </ReactFlow>
          </div>}

        {/* About Section */}
        <div>
          <div className="mb-3 flex items-center gap-2">
            <IconCircleInfoOutline18 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-ds-text-primary text-xl font-semibold">About</h2>
          </div>
          <p className="text-ds-text-secondary mb-4">
            {isTrigger_0 ? "This trigger will activate your workflow when specific conditions are met." : "This action will execute the specified operation when your workflow runs."}
          </p>

          {/* Tags */}
          {integration.tags && integration.tags.length > 0 && <div className="flex flex-wrap gap-2">
              {integration.tags.map((tag: string) => <Link key={tag} href={`/integrations/tag/${tag}`} className="bg-ds-neutral-100 text-ds-text-secondary hover:bg-ds-neutral-200 dark:bg-ds-neutral-800 dark:hover:bg-ds-neutral-700 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors">
                  {tag}
                </Link>)}
            </div>}
        </div>

        {/* Connector Requirements */}
        {integration.connector && <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <IconCodeOutline18 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-ds-text-primary text-xl font-semibold">
                Connect with {formatConnectorName(integration.connector.type)}
              </h2>
            </div>
            <p className="text-ds-text-secondary mb-2">
              This integration requires a connector to be configured before it can be used in workflows.
            </p>
            <Link href="/connectors" className="group flex items-center gap-4">
              <ConnectorLogo type={integration.connector.type} name={integration.connector.type} logoUrl={connectorTypeDefinition?.logoUrl} />
              <div className="flex-1 text-left">
                <div className="text-ds-text-primary font-medium">
                  {formatConnectorName(integration.connector.type)}
                </div>
                <div className="flex items-center gap-1 text-sm text-blue-600 group-hover:text-blue-600 dark:text-blue-400 dark:group-hover:text-blue-400">
                  Configure connector <IconArrowRightOutline18 className="h-3 w-3" />
                </div>
              </div>
            </Link>
          </div>}

        {/* Input Fields - Use specialized component for x402 endpoints */}
        {isX402Endpoint ? <X402InputSection integration={integration} previewValues={previewValues} setPreviewValues={setPreviewValues} formKey={formKey} setFormKey={setFormKey} searchQuery={searchQuery} /> : <IntegrationInputFields allFields={visibleFieldNames} requiredFields={requiredFields} filteredFields={filteredFields} previewValues={previewValues} setPreviewValues={setPreviewValues} formKey={formKey} setFormKey={setFormKey} />}

        {/* Output Fields - Use specialized component for x402 endpoints */}
        {isX402Endpoint ? <X402OutputSection integration={integration} searchQuery={searchQuery} exampleOutput={exampleOutput} /> : <IntegrationOutputFields resultFields={resultFields} resultRequiredFields={resultRequiredFields} resultProperties={(integration.resultSchema?.properties || {}) as Record<string, JsonSchemaProperty>} />}

        {/* API Schema Section - Collapsible JSON response for actions */}
        {integrationType === "action" && actionApiResponse && <Accordion type="single" collapsible>
            <AccordionItem value="api-schema" className="border-0">
              <AccordionTrigger className="py-0 hover:no-underline">
                <div className="flex items-center gap-2">
                  <IconCodeOutline18 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  <h2 className="text-ds-text-primary text-lg font-semibold">API Schema</h2>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <div className="border-ds-border-default dark:bg-ds-neutral-900 overflow-hidden rounded-lg border bg-zinc-50">
                  <div className="max-h-[500px] overflow-auto">
                    <SyntaxHighlighter language="json" style={syntaxStyle} customStyle={{
                  margin: 0,
                  padding: "1rem",
                  fontSize: "0.8125rem",
                  lineHeight: "1.6",
                  background: "transparent"
                }} showLineNumbers wrapLines>
                      {JSON.stringify(actionApiResponse, null, 2)}
                    </SyntaxHighlighter>
                  </div>
                  <div className="mt-2 flex justify-end">
                    <CopyJsonButton value={JSON.stringify(actionApiResponse, null, 2)} />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>}

        {/* Comments Section - Real-time discussion using Ably Chat */}
        <div className="mt-8">
          <CommentsSection roomId={`integration:${integrationType}:${type}`} title="Discussion" />
        </div>
      </div>
    </PageLayout>;
}
function IntegrationDetailSkeleton() {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-14 w-14 rounded-xl" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-start gap-4">{t0}<div className="flex-1 space-y-3"><Skeleton className="h-7 w-64" /><Skeleton className="h-4 w-full max-w-xl" /><Skeleton className="h-4 w-3/4 max-w-md" /></div><Skeleton className="h-9 w-28 rounded-lg" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Skeleton className="h-5 w-24" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="space-y-4">{t2}<div className="border-ds-border-default space-y-4 rounded-xl border p-6">{Array.from({
          length: 4
        }).map(_temp9)}</div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Skeleton className="h-5 w-24" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="animate-in fade-in mx-auto w-full max-w-5xl space-y-8 px-6 py-10 duration-300">{t1}{t3}<div className="space-y-4">{t4}<div className="border-ds-border-default space-y-3 rounded-xl border p-6">{Array.from({
            length: 3
          }).map(_temp10)}</div></div></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
function _temp10(__0, i_0) {
  return <div key={i_0} className="flex items-center gap-3"><Skeleton className="h-4 w-40" /><Skeleton className="h-4 w-20" /><Skeleton className="h-4 flex-1" /></div>;
}
function _temp9(_, i) {
  return <div key={i} className="space-y-2"><Skeleton className="h-3 w-32" /><Skeleton className="h-9 w-full rounded-md" /></div>;
}